import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingCart,
  Trash2,
  Tag,
  Zap,
  Receipt,
  Ticket,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Store,
  PenSquare,
  Lock,
  Gamepad2,
  Wallet,
  Building2,
  Check,
  Clock,
  RotateCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  UserPlus,
  Bell,
  X,
} from 'lucide-react';
import { useCartStore } from '../../stores/useCartStore';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useUIStore } from '../../stores/useUIStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';
import { useOrderStore } from '../../stores/useOrderStore';
import { orderApi, OrderPaymentMethod } from '../../services/orderApi';
import {
  checkFriendshipStatus,
  sendBotFriendRequests,
  notifyShortage,
  isRechargeItem,
  FriendshipCheckResponse,
} from '../../services/checkApi';
import { SupportedCurrency } from '../../types';
import { calculateFiatPrice } from '../../utils/currency';
import { cn } from '../../utils/cn';

interface LinkedAccount {
  id?: number;
  username: string;
  account_id?: string;
  fortnite_account_id?: string;
  platform?: string;
  is_primary?: boolean;
}

export const CartPage: React.FC = () => {
  const items = useCartStore((s) => s.items);
  const removeItem = useCartStore((s) => s.removeItem);
  const clearCart = useCartStore((s) => s.clearCart);
  const appliedDiscount = useCartStore((s) => s.appliedDiscount);
  const applyCoupon = useCartStore((s) => s.applyCoupon);
  const removeCoupon = useCartStore((s) => s.removeCoupon);
  const getTotals = useCartStore((s) => s.getTotals);

  const totalItemCount = items.reduce((sum, it) => sum + (it.quantity || 1), 0);

  const currency = useCurrencyStore((s) => s.currency);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);

  const navigateTo = useUIStore((s) => s.navigateTo);
  const openAuth = useUIStore((s) => s.openAuthModal);

  const authUser = useAuthStore((s) => s.user);

  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [showCouponsList, setShowCouponsList] = useState(false);
  const [userCoupons, setUserCoupons] = useState<any[]>([]);

  useEffect(() => {
    const token =
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token');
    if (token) {
      fetch('/api/coupons/my-coupons', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((data) => {
          if (Array.isArray(data)) {
            setUserCoupons(data.filter((c: any) => c.can_use));
          }
        })
        .catch(() => {});
    }
  }, [authUser]);

  // Currency modal state
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [isCurrencyModalClosing, setIsCurrencyModalClosing] = useState(false);
  const [tempCurrency, setTempCurrency] = useState<SupportedCurrency>(currency);

  const closeCurrencyModal = useCallback(() => {
    if (isCurrencyModalClosing) return;
    setIsCurrencyModalClosing(true);
    setTimeout(() => {
      setIsCurrencyModalOpen(false);
      setIsCurrencyModalClosing(false);
    }, 160);
  }, [isCurrencyModalClosing]);

  useEffect(() => {
    if (isCurrencyModalOpen) {
      lockScroll();
      return () => {
        unlockScroll();
      };
    }
  }, [isCurrencyModalOpen]);

  // Cascade Checkout Steps state (1, 2, 3)
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [selectedAccountUsername, setSelectedAccountUsername] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<OrderPaymentMethod>('TRANSFERENCIA_BANCARIA');
  const [userManuallySelectedMethod, setUserManuallySelectedMethod] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // Pending order banner state
  const [pendingOrderCode, setPendingOrderCode] = useState<string | null>(null);

  // Linked accounts for Step 1
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);

  // User state
  const user = authUser || (() => {
    try {
      const raw = localStorage.getItem('fn_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  // Synchronize linked accounts
  useEffect(() => {
    const loadAccounts = async () => {
      let accounts: LinkedAccount[] = [];
      try {
        const raw = localStorage.getItem('fn_linked_accounts');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            accounts = parsed.map((a: any) => ({
              id: a.id,
              username: a.username || a.fortnite_username || '',
              account_id: a.account_id || a.fortnite_account_id || '',
              fortnite_account_id: a.fortnite_account_id || a.account_id || '',
              platform: a.platform || 'Epic Games',
              is_primary: Boolean(a.is_primary || a.is_default),
            }));
          }
        }
      } catch {}

      const token = localStorage.getItem('fn_token') || localStorage.getItem('token');
      if (token) {
        try {
          const res = await fetch('/api/auth/me/fortnite-accounts', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) {
              accounts = data.map((acc: any) => ({
                id: acc.id,
                username: acc.fortnite_username,
                account_id: acc.fortnite_account_id || '',
                fortnite_account_id: acc.fortnite_account_id || '',
                platform: 'Epic Games',
                is_primary: Boolean(acc.is_default),
              }));
              localStorage.setItem('fn_linked_accounts', JSON.stringify(accounts));
            }
          }
        } catch {}
      }

      if (accounts.length === 0 && user?.fortnite_username) {
        accounts = [
          {
            username: user.fortnite_username,
            account_id: user.fortnite_account_id || '',
            fortnite_account_id: user.fortnite_account_id || '',
            platform: 'Epic Games',
            is_primary: true,
          },
        ];
      }

      setLinkedAccounts(accounts);
      if (accounts.length > 0) {
        const primary = accounts.find((a) => a.is_primary) || accounts[0];
        setSelectedAccountUsername(primary.username);
      } else if (user?.fortnite_username) {
        setSelectedAccountUsername(user.fortnite_username);
      }
    };

    loadAccounts();
  }, [user]);

  // Friendship & Gift Eligibility Verification State (Copiado 1:1 de frontend original)
  const [verificationData, setVerificationData] = useState<FriendshipCheckResponse | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [step1Mode, setStep1Mode] = useState<
    'CONFIRM' | 'WAITING_48H' | 'ADD_BOTS' | 'REQUESTS_SENT' | 'RESENT' | 'NOTIFY' | 'NOTIFIED'
  >('CONFIRM');
  const [isSendingBotRequests, setIsSendingBotRequests] = useState(false);
  const [isNotifyingShortage, setIsNotifyingShortage] = useState(false);
  const [privacyBlockedMessage, setPrivacyBlockedMessage] = useState<string | null>(null);
  const [hasJustResent, setHasJustResent] = useState(false);

  // Verificación reactiva de amistad con bots de regalo
  const verifySelectedAccount = useCallback(
    async (targetUsername?: string) => {
      const username = (targetUsername || selectedAccountUsername || user?.fortnite_username || '').trim();
      if (!username || items.length === 0) return;

      const cosmeticEntries = items.filter((it) => !isRechargeItem(String(it.offerId || it.id || '')));

      // Si todos los objetos son recargas directas, no requieren 48hs de bots
      if (cosmeticEntries.length === 0) {
        setVerificationData({
          eligible: true,
          status: 'RECHARGE_ONLY' as any,
          user: {
            display_name: username,
            account_id: `recharge_${username.toLowerCase()}`,
            platform: 'Epic Games',
            platform_label: 'Epic Games',
            searched_name: username,
          },
          available_vbucks: 99999,
          needed_vbucks: 0,
          available_gifts: 99,
          needed_gifts: 0,
          message: 'Recarga Directa: Tu pedido no requiere 48 hs de amistad con bots. La carga se coordina por WhatsApp tras el pago.',
        });
        setStep1Mode('CONFIRM');
        return;
      }

      const selectedAcc = linkedAccounts.find(
        (a) => a.username.toLowerCase() === username.toLowerCase()
      );
      const accountId = selectedAcc?.fortnite_account_id || selectedAcc?.account_id || '';

      setIsVerifying(true);
      setPrivacyBlockedMessage(null);

      try {
        const totalVbucks = cosmeticEntries.reduce((acc, cur) => acc + (cur.vbucks || 0), 0);
        const itemsPayload = cosmeticEntries.map((it) => ({
          vbucks: it.vbucks,
          offer_id: it.offerId,
          name: it.name,
        }));

        const data = await checkFriendshipStatus({
          username,
          account_id: accountId || undefined,
          total_vbucks: totalVbucks,
          items: itemsPayload,
        });

        setVerificationData(data);

        const hasPending =
          data.status === 'REQUEST_SENT' ||
          Boolean((data.pending_bots_count ?? 0) > 0 && !data.eligible);
        const pavosExceeded =
          !hasPending &&
          (data.status === 'NO_BOT_BALANCE' || data.status === 'NO_BOT_ITEM_BALANCE');
        const giftsExceeded = !hasPending && data.status === 'BOT_LIMIT_REACHED';

        if (data.status === 'FRIENDS_READY' && data.eligible) {
          setHasJustResent(false);
          setStep1Mode('CONFIRM');
        } else if (hasPending) {
          setStep1Mode(hasJustResent ? 'RESENT' : 'REQUESTS_SENT');
        } else if (data.status === 'NOT_FRIENDS') {
          setStep1Mode('ADD_BOTS');
        } else if (data.status === 'FRIENDS_WAITING_48H') {
          const hasMoreBots = Boolean(data.can_invite_more && (data.unadded_bots_count ?? 0) > 0);
          if (hasMoreBots) {
            setStep1Mode('ADD_BOTS');
          } else {
            setStep1Mode('WAITING_48H');
          }
        } else if (pavosExceeded || giftsExceeded) {
          const hasMoreBots = Boolean(data.can_invite_more && (data.unadded_bots_count ?? 0) > 0);
          if (hasMoreBots) {
            setStep1Mode('ADD_BOTS');
          } else {
            setStep1Mode('NOTIFY');
          }
        } else if (data.eligible) {
          setStep1Mode('CONFIRM');
        } else {
          setStep1Mode('WAITING_48H');
        }

        // Si la cuenta deja de ser elegible, volver al paso 1
        if (!data.eligible) {
          setCurrentStep(1);
        }
      } catch (err: any) {
        setVerificationData({
          eligible: false,
          status: 'NOT_FRIENDS',
          user: {
            display_name: username,
            account_id: '',
            platform: 'Epic Games',
            platform_label: 'Epic Games',
            searched_name: username,
          },
          available_vbucks: 0,
          needed_vbucks: 0,
          available_gifts: 0,
          needed_gifts: 0,
          message: err.message || 'Error al comprobar disponibilidad con los bots.',
        });
        setStep1Mode('CONFIRM');
        setCurrentStep(1);
      } finally {
        setIsVerifying(false);
      }
    },
    [selectedAccountUsername, user, items, linkedAccounts, hasJustResent]
  );

  // Trigger comprobación cada vez que cambia la cuenta seleccionada o el carrito
  useEffect(() => {
    if (selectedAccountUsername || user?.fortnite_username) {
      verifySelectedAccount();
    }
  }, [selectedAccountUsername, user?.fortnite_username, items.length]);

  // Reenviar solicitudes de amistad de todos los bots
  const handleAddMoreBots = async () => {
    const username = (selectedAccountUsername || user?.fortnite_username || '').trim();
    if (!username) return;

    const selectedAcc = linkedAccounts.find(
      (a) => a.username.toLowerCase() === username.toLowerCase()
    );
    const accountId = selectedAcc?.fortnite_account_id || selectedAcc?.account_id || '';

    setIsSendingBotRequests(true);
    setPrivacyBlockedMessage(null);
    try {
      const res = await sendBotFriendRequests(username, accountId);
      if (res.privacy_blocked) {
        setPrivacyBlockedMessage(
          'Tu cuenta de Epic Games no acepta solicitudes de amistad. Ve a Ajustes > Cuenta y Privacidad en Fortnite y pon Permiso de solicitudes de amistad en Cualquiera.'
        );
        setStep1Mode('ADD_BOTS');
        return;
      }
      setHasJustResent(true);
      setStep1Mode('RESENT');
      await verifySelectedAccount();
    } catch (err: any) {
      alert(err.message || 'No se pudieron enviar las solicitudes de amistad.');
      setStep1Mode('ADD_BOTS');
    } finally {
      setIsSendingBotRequests(false);
    }
  };

  // Notificar falta de stock al administrador
  const handleNotifyShortage = async () => {
    const username = (selectedAccountUsername || user?.fortnite_username || '').trim();
    if (!username) return;

    const selectedAcc = linkedAccounts.find(
      (a) => a.username.toLowerCase() === username.toLowerCase()
    );
    const accountId = selectedAcc?.fortnite_account_id || selectedAcc?.account_id || '';
    const cosmeticEntries = items.filter((it) => !isRechargeItem(String(it.offerId || it.id || '')));
    const totalVbucks = cosmeticEntries.reduce((acc, cur) => acc + (cur.vbucks || 0), 0);

    setIsNotifyingShortage(true);
    try {
      await notifyShortage({
        username,
        account_id: accountId || undefined,
        needed_vbucks: verificationData?.needed_vbucks || totalVbucks,
        available_vbucks: verificationData?.available_vbucks || 0,
        needed_gifts: verificationData?.needed_gifts || cosmeticEntries.length,
        available_gifts: verificationData?.available_gifts || 0,
        items: cosmeticEntries.map((it) => ({
          vbucks: it.vbucks,
          offer_id: it.offerId,
          name: it.name,
        })),
      });
      setStep1Mode('NOTIFIED');
    } catch (err: any) {
      alert(err.message || 'Error al notificar falta de stock.');
    } finally {
      setIsNotifyingShortage(false);
    }
  };

  // Acción del botón Paso 1
  const handleStep1Action = async () => {
    if (step1Mode === 'CONFIRM') {
      if (!verificationData?.eligible) {
        alert(
          verificationData?.message ||
            'Tu cuenta de Fortnite aún no está lista para recibir regalos de nuestros bots.'
        );
        return;
      }
      setCurrentStep(2);
    } else if (step1Mode === 'ADD_BOTS' || step1Mode === 'REQUESTS_SENT') {
      await handleAddMoreBots();
    } else if (step1Mode === 'NOTIFY') {
      await handleNotifyShortage();
    }
  };

  // Check for any active pending order for banner
  useEffect(() => {
    const checkPendingOrder = async () => {
      try {
        const lastRaw = localStorage.getItem('fn_last_order');
        if (lastRaw) {
          const last = JSON.parse(lastRaw);
          if (last.order_code) {
            const ord = await orderApi.getOrder(last.order_code);
            if (ord.status === 'PENDIENTE_PAGO') {
              setPendingOrderCode(ord.order_code);
              return;
            }
          }
        }
      } catch {}
    };

    checkPendingOrder();
  }, []);

  const totals = getTotals(currency);

  const handleApplyCoupon = async (codeToApply?: string) => {
    const code = (codeToApply || couponInput).trim().toUpperCase();
    if (!code) return;
    try {
      const res = await applyCoupon(code);
      if (res.success) {
        setCouponError(null);
        setCouponInput('');
      } else {
        setCouponError(res.message || 'Cupón no válido.');
      }
    } catch {
      setCouponError('Error al validar el cupón.');
    }
  };

  const openCurrencyModal = () => {
    setTempCurrency(currency);
    setIsCurrencyModalOpen(true);
  };

  // Step 2: Confirm Payment Method
  const handleConfirmStep2 = () => {
    setCurrentStep(3);
  };

  // Step 3: Final Submit Order
  const handleFinalSubmitOrder = async () => {
    if (items.length === 0) return;
    setIsSubmittingOrder(true);
    setOrderError(null);

    const payloadItems = items.flatMap((it) => {
      const qty = Math.max(1, it.quantity || 1);
      return Array.from({ length: qty }, () => ({
        offer_id: it.offerId,
        offerId: it.offerId,
        name: it.name,
        vbucks: it.vbucks,
        image: it.image,
        image_url: it.image,
      }));
    });

    try {
      const targetUser = selectedAccountUsername.trim() || user?.fortnite_username || 'godblezzed';
      const willUseWallet =
        selectedPaymentMethod === 'SALDO_BILLETERA'
          ? true
          : canUseSplitWallet && useWalletBalance && vbucksToUse > 0;

      const createdOrder = await orderApi.createOrder({
        customer_username: targetUser,
        currency,
        payment_method: selectedPaymentMethod,
        items: payloadItems,
        discount_code: appliedDiscount?.code || undefined,
        customer_email: user?.email || undefined,
        use_wallet_balance: willUseWallet,
      });

      // Refrescar el usuario para actualizar el saldo en tiempo real
      await useAuthStore.getState().fetchMe();

      // Save last order in localStorage
      try {
        localStorage.setItem(
          'fn_last_order',
          JSON.stringify({
            order_code: createdOrder.order_code,
            created_at: new Date().toISOString(),
          })
        );
      } catch {}

      // Clear Cart
      clearCart();

      // Set active order code explicitly in UI store and Order store
      const cleanCode = createdOrder.order_code.replace('#', '').trim().toUpperCase();
      useUIStore.getState().setActiveOrderCode(cleanCode);
      useOrderStore.setState({ activeOrder: createdOrder });

      // Navigate SPA directly to Order Page
      navigateTo('order', `/order.html?code=${cleanCode}`);
    } catch (err: any) {
      setOrderError(err.message || 'Error al generar el pedido. Intenta nuevamente.');
      setIsSubmittingOrder(false);
    }
  };

  const hasRecharge = items.some(
    (it: any) =>
      it.category === 'Recargas' ||
      String(it.offerId || it.id || '').startsWith('recharge_') ||
      ['recharge_vbucks_12500', 'recharge_crew', 'crew'].includes(String(it.offerId || it.id || ''))
  );

  const [useWalletBalance, setUseWalletBalance] = useState(true);

  const userBalance = user?.balance_vbucks || 0;
  const isWalletEligible = !hasRecharge && userBalance >= totals.totalVbucks && totals.totalVbucks > 0;

  useEffect(() => {
    if (hasRecharge && selectedPaymentMethod === 'SALDO_BILLETERA') {
      setSelectedPaymentMethod('TRANSFERENCIA_BANCARIA');
    } else if (isWalletEligible && !userManuallySelectedMethod && selectedPaymentMethod !== 'SALDO_BILLETERA') {
      setSelectedPaymentMethod('SALDO_BILLETERA');
    } else if (!isWalletEligible && selectedPaymentMethod === 'SALDO_BILLETERA') {
      setSelectedPaymentMethod('TRANSFERENCIA_BANCARIA');
    }
  }, [hasRecharge, isWalletEligible, userManuallySelectedMethod, selectedPaymentMethod]);
  const canUseSplitWallet = !hasRecharge && Boolean(user && userBalance > 0 && userBalance < totals.totalVbucks && totals.totalVbucks > 0);
  const vbucksToUse = canUseSplitWallet && useWalletBalance ? Math.min(userBalance, totals.totalVbucks) : 0;

  const walletMoneyDiscount = vbucksToUse > 0 ? calculateFiatPrice(vbucksToUse, currency) : 0;
  const remainingBaseFiat = Math.max(0, totals.basePrice - walletMoneyDiscount);
  const discountPct = appliedDiscount?.discount_pct || 0;
  const calculatedDiscountAmount = appliedDiscount
    ? Math.round(remainingBaseFiat * (discountPct / 100))
    : 0;
  const finalPayableFiat = Math.max(0, remainingBaseFiat - calculatedDiscountAmount);

  const displayFinalPrice =
    selectedPaymentMethod === 'SALDO_BILLETERA'
      ? 0
      : selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' && canUseSplitWallet && useWalletBalance
      ? finalPayableFiat
      : totals.finalPrice;

  return (
    <div className="w-full min-h-screen bg-transparent text-[#06173f] py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-[1200px] mx-auto space-y-6 sm:space-y-8">
        {/* Cabecera de Página */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-[var(--line)] pb-5">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black italic tracking-tight text-[#06173f] font-heading flex items-center gap-3">
              <ShoppingCart className="w-8 h-8 text-[#1447e6]" />
              <span>Tu Carrito de Regalos</span>
            </h1>
            <p className="text-xs sm:text-sm text-[var(--soft)] mt-1 font-medium">
              Revisa tus cosméticos seleccionados, verifica tu cuenta de Epic Games y completa tu compra.
            </p>
          </div>

          {/* Selector de Moneda - Abre modal oficial ARS / MXN */}
          <button
            type="button"
            onClick={openCurrencyModal}
            className="flex items-center gap-2 px-3.5 py-2 bg-white border-2 border-[var(--line)] text-xs font-heading font-black text-[#06173f] hover:bg-slate-50 transition self-start sm:self-auto shadow-sm cursor-pointer"
            style={{
              clipPath:
                'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
            }}
            title="Cambiar moneda"
          >
            <span className="text-base leading-none">
              {currency === 'MXN' ? '🇲🇽' : '🇦🇷'}
            </span>
            <span className="font-heading font-black">{currency}</span>
            <PenSquare className="w-3.5 h-3.5 text-[#1447e6]" />
          </button>
        </div>

        {/* BANNER DE PEDIDO PENDIENTE ACTIVO */}
        {pendingOrderCode && (
          <div
            className="pn flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-white border-2 border-amber-300 shadow-sm"
            style={{
              filter: 'drop-shadow(3px 3px 0 #fef3c7)',
            }}
          >
            <div className="flex items-center gap-3.5">
              <div
                className="w-11 h-11 bg-amber-100 text-amber-600 flex items-center justify-center text-xl shrink-0 font-heading"
                style={{ clipPath: 'var(--cut)' }}
              >
                <Clock className="w-6 h-6 animate-pulse" />
              </div>
              <div>
                <h4 className="font-black text-sm uppercase text-amber-900 font-heading">
                  Tienes un pedido pendiente en curso ({pendingOrderCode.startsWith('#') ? pendingOrderCode : `#${pendingOrderCode}`})
                </h4>
                <p className="text-xs text-amber-800 font-medium">
                  Reserva de 1 hora activa. Sube tu comprobante de pago para autorizar el despacho de tus regalos.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigateTo('order', `/order.html?code=${pendingOrderCode}`)}
              className="btn sm self-start sm:self-auto cursor-pointer flex items-center gap-1.5"
              style={{ background: '#f59e0b', color: '#fff' }}
            >
              <span>Cargar Comprobante</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Estado Vacío */}
        {items.length === 0 ? (
          <div
            className="p-10 sm:p-14 bg-white border-2 border-[var(--line)] text-center max-w-xl mx-auto space-y-4 shadow-sm"
            style={{
              clipPath:
                'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
            }}
          >
            <div
              className="w-16 h-16 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] flex items-center justify-center mx-auto"
              style={{
                clipPath:
                  'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
              }}
            >
              <ShoppingCart className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-black uppercase text-[#06173f] font-heading tracking-tight">
              Tu Carrito está Vacío
            </h3>
            <p className="text-xs sm:text-sm text-[var(--soft)] max-w-sm mx-auto font-medium">
              Explora la tienda diaria de Fortnite, agrega tus skins y lotes favoritos con descuento y recíbelos al instante.
            </p>
            <button
              type="button"
              onClick={() => navigateTo('shop', '/tienda')}
              className="btn font-heading font-black cursor-pointer inline-flex items-center gap-2"
            >
              <Store className="w-4 h-4" />
              <span>Ver Tienda de Objetos</span>
            </button>
          </div>
        ) : (
          /* Grid de Carrito con Items y Checkout */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Columna Izquierda: Lista de Ítems (7 cols) */}
            <div className="lg:col-span-7 space-y-4">
              <div className="flex items-center justify-between pb-2">
                <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe]">
                  {totalItemCount} {totalItemCount === 1 ? 'OBJETO EN EL CARRITO' : 'OBJETOS EN EL CARRITO'}
                </span>
                <button
                  type="button"
                  onClick={clearCart}
                  className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1.5 transition cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Vaciar Carrito</span>
                </button>
              </div>

              {/* Items List */}
              {items.some(
                (it) =>
                  it.offerId?.startsWith('recharge_') ||
                  it.offerId === 'recharge_crew' ||
                  it.offerId === 'recharge_vbucks_12500' ||
                  (it as any).category === 'Recargas'
              ) && (
                <div
                  className="bg-amber-50 border-2 border-amber-300 p-3 text-xs text-amber-900 font-medium flex items-center gap-3"
                  style={{
                    clipPath:
                      'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                  }}
                >
                  <i className="fa-brands fa-whatsapp text-emerald-600 text-xl shrink-0" />
                  <span>
                    <b className="font-heading uppercase">Coordinación por WhatsApp:</b> Tu carrito
                    incluye recargas directas a tu cuenta. La carga se coordina por WhatsApp tras el pago.
                  </span>
                </div>
              )}

              <div className="space-y-3">
                {items.map((item) => {
                  const itemQty = item.quantity || 1;
                  const unitFiat = calculateFiatPrice(item.vbucks, currency, item.offerId);
                  const totalLineFiat = unitFiat * itemQty;
                  const totalLineVbucks = item.vbucks * itemQty;

                  const isRechargeVbucks =
                    item.offerId === 'recharge_vbucks_12500' ||
                    item.offerId === 'recharge_crew' ||
                    item.offerId?.startsWith('recharge_') ||
                    item.image?.includes('cid_016_athena_commando_f') ||
                    item.image?.includes('character_faintdoor');
                  const displayImage = isRechargeVbucks
                    ? 'https://fortnite-api.com/images/vbuck.png'
                    : item.image || item.images?.icon || item.images?.featured;

                  return (
                    <div
                      key={item.offerId}
                      className="p-3 sm:p-4 bg-white border-2 border-[var(--line)] flex items-center justify-between gap-3 shadow-sm hover:border-[#1447e6] transition"
                      style={{
                        clipPath:
                          'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-900 shrink-0 border border-slate-700 overflow-hidden relative">
                          {displayImage ? (
                            <img
                              src={displayImage}
                              alt={item.name}
                              className={cn(
                                'w-full h-full',
                                isRechargeVbucks ? 'object-contain p-1.5' : 'object-cover'
                              )}
                              onError={(e) => {
                                e.currentTarget.src = '/vbuck.png';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500">
                              <ShoppingCart className="w-6 h-6" />
                            </div>
                          )}
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-heading font-black text-xs sm:text-sm text-[#06173f] truncate uppercase tracking-wide">
                              {item.name}
                            </h4>
                            {itemQty > 1 && (
                              <span className="px-1.5 py-0.5 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] text-[10px] font-black font-heading shrink-0">
                                x{itemQty}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs font-heading font-bold text-[#1447e6]">
                              {totalLineVbucks.toLocaleString('es-AR')} Pavos
                            </span>
                            {itemQty > 1 && (
                              <span className="text-[10px] text-[var(--soft)] font-medium">
                                ({item.vbucks.toLocaleString('es-AR')} c/u)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className="text-xs sm:text-sm font-black font-heading text-[#06173f]">
                            ${totalLineFiat.toLocaleString('es-AR')} {currency}
                          </div>
                          {itemQty > 1 && (
                            <div className="text-[10px] text-[var(--soft)] font-medium">
                              (${unitFiat.toLocaleString('es-AR')} c/u)
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.offerId)}
                          className="p-1.5 text-slate-400 hover:text-red-600 transition cursor-pointer"
                          title="Eliminar objeto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Columna Derecha: Resumen del Pedido & Checkout en Cascada (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              {/* TARJETA 1: RESUMEN DEL PEDIDO (Captura 1) */}
              <div
                className="pn bg-white border-2 border-[var(--line)] p-5 sm:p-6 space-y-4 shadow-sm"
                id="summary-card"
              >
                <h2 className="text-base sm:text-lg font-black uppercase tracking-wider text-[#06173f] border-b-2 border-[var(--line)] pb-3 flex items-center gap-2 font-heading">
                  <Receipt className="w-5 h-5 text-[#1447e6]" />
                  <span>RESUMEN DEL PEDIDO</span>
                </h2>

                <div className="space-y-3 text-xs sm:text-sm">
                  {/* Total Pavos */}
                  <div className="flex justify-between text-[var(--soft)] font-medium">
                    <span className="flex items-center gap-1.5">
                      <span className="w-4 h-4 bg-[#eff6ff] text-[#1447e6] rounded flex items-center justify-center text-[10px] font-bold italic font-heading">
                        V
                      </span>
                      <span>Total Pavos:</span>
                    </span>
                    <span className="font-black text-[#06173f] font-heading text-sm">
                      {totals.totalVbucks.toLocaleString('es-AR')}
                    </span>
                  </div>

                  {/* Subtotal */}
                  <div className="flex justify-between text-[var(--soft)] font-medium">
                    <span>Subtotal:</span>
                    <span className="font-bold text-[#06173f] font-heading">
                      ${totals.basePrice.toLocaleString('es-AR')} {currency}
                    </span>
                  </div>

                  {/* Fila Descuento por Saldo en Cuenta */}
                  {selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' &&
                    canUseSplitWallet &&
                    useWalletBalance &&
                    vbucksToUse > 0 && (
                      <div className="flex justify-between text-[#1447e6] font-bold">
                        <div className="flex items-center gap-1.5">
                          <Wallet className="w-3.5 h-3.5 text-[#1447e6]" />
                          <span>Saldo aplicado (-{vbucksToUse.toLocaleString('es-AR')} G-Coins):</span>
                        </div>
                        <span>-${walletMoneyDiscount.toLocaleString('es-AR')} {currency}</span>
                      </div>
                    )}

                  {/* Fila Descuento si está aplicado */}
                  {appliedDiscount && (
                    <div className="flex justify-between text-emerald-600 font-bold">
                      <div className="flex items-center gap-1.5">
                        <span>Descuento ({appliedDiscount.code}):</span>
                        <button
                          type="button"
                          onClick={() => removeCoupon()}
                          className="text-[10px] text-red-500 hover:underline cursor-pointer"
                        >
                          (Quitar)
                        </button>
                      </div>
                      <span>
                        -$
                        {(selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' &&
                        canUseSplitWallet &&
                        useWalletBalance &&
                        vbucksToUse > 0
                          ? calculatedDiscountAmount
                          : totals.discountAmount
                        ).toLocaleString('es-AR')}{' '}
                        {currency}
                      </span>
                    </div>
                  )}

                  {/* Sección Cupón de Descuento (Captura 1) */}
                  <div className="pt-2 border-t-2 border-[var(--line)] space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-bold text-[var(--soft)] uppercase tracking-wider flex items-center gap-1.5 font-heading">
                        <Tag className="w-3.5 h-3.5 text-[#1447e6]" />
                        <span>CUPÓN DE DESCUENTO</span>
                      </label>

                      <button
                        type="button"
                        onClick={async () => {
                          if (userCoupons.length > 0) {
                            handleApplyCoupon(userCoupons[0].code);
                          } else {
                            handleApplyCoupon('PROMO15');
                          }
                        }}
                        className="text-[11px] font-bold text-[#1447e6] hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <Zap className="w-3 h-3 text-[#1447e6]" />
                        <span>Mejor cupón</span>
                      </button>
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleApplyCoupon();
                          }
                        }}
                        placeholder="EJ: PROMO15 O REV-..."
                        className="flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#f8fafc] border-2 border-[var(--line)] text-[#06173f] focus:outline-none focus:border-[#1447e6]"
                        style={{
                          clipPath:
                            'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleApplyCoupon()}
                        className="bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black px-4 py-2 uppercase tracking-wider text-xs transition shadow-sm cursor-pointer"
                        style={{
                          clipPath:
                            'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                        }}
                      >
                        Aplicar
                      </button>
                    </div>

                    {couponError && (
                      <p className="text-[11px] font-semibold text-red-600">
                        {couponError}
                      </p>
                    )}

                    {/* Acordeón Cupones Disponibles */}
                    {userCoupons.length > 0 && (
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={() => setShowCouponsList(!showCouponsList)}
                          className="text-[11px] font-bold text-[var(--soft)] hover:text-[#06173f] flex items-center gap-1 transition cursor-pointer"
                        >
                          <Ticket className="w-3.5 h-3.5 text-[#1447e6]" />
                          <span>Mis cupones disponibles ({userCoupons.length})</span>
                          {showCouponsList ? (
                            <ChevronUp className="w-3 h-3 text-[var(--soft)]" />
                          ) : (
                            <ChevronDown className="w-3 h-3 text-[var(--soft)]" />
                          )}
                        </button>

                        {showCouponsList && (
                          <div className="mt-2 p-2 bg-[#f8fafc] border border-[var(--line)] space-y-2 text-xs">
                            {userCoupons.map((c) => (
                              <div
                                key={c.id || c.code}
                                className="border-b border-[var(--line)] pb-1.5 last:border-b-0 last:pb-0 space-y-0.5"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-[#1447e6] font-heading">
                                    {c.code} ({c.discount_pct ? `-${c.discount_pct}%` : `-$${c.discount_value}`})
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleApplyCoupon(c.code)}
                                    className="text-[10px] font-bold text-[#1447e6] underline cursor-pointer"
                                  >
                                    Usar
                                  </button>
                                </div>
                                <p className="text-[10px] text-[var(--soft)]">
                                  {c.title || 'Válido para todas las compras.'}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Fila Total Final (Captura 1) */}
                  <div className="border-t-2 border-[var(--line)] pt-3 flex justify-between items-baseline">
                    <span className="text-xs sm:text-sm font-black uppercase tracking-wide text-[#06173f] font-heading">
                      TOTAL A PAGAR:
                    </span>
                    <div className="text-right">
                      <div className="text-2xl sm:text-3xl font-black text-[#1447e6] font-heading">
                        ${displayFinalPrice.toLocaleString('es-AR')} {currency}
                      </div>
                      <span className="text-[10px] text-[var(--soft)] font-medium">
                        {selectedPaymentMethod === 'SALDO_BILLETERA'
                          ? `Abonado 100% con saldo (${totals.totalVbucks.toLocaleString('es-AR')} Pavos)`
                          : `Cotización calculada para ${currency}`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Si no está logueado, prompt para iniciar sesión */}
                {!user && (
                  <div className="pt-3 border-t-2 border-[var(--line)] space-y-2.5">
                    <button
                      type="button"
                      onClick={() => openAuth('login')}
                      className="btn w-full flex items-center justify-center gap-2 cursor-pointer"
                      style={{ width: '100%' }}
                    >
                      <Lock className="w-4 h-4" />
                      <span>INICIAR SESIÓN PARA CONTINUAR</span>
                    </button>
                    <p className="text-xs text-[var(--soft)] text-center font-medium">
                      ¿No tienes cuenta?{' '}
                      <button
                        type="button"
                        onClick={() => openAuth('register')}
                        className="text-[#1447e6] hover:underline font-bold cursor-pointer"
                      >
                        Crear una cuenta
                      </button>
                    </p>
                  </div>
                )}
              </div>

              {/* CHECKOUT EN CASCADA 1:1 (Pasos 1, 2, 3) - Visible cuando está logueado */}
              {user && (
                <div className="space-y-5">
                  {/* PASO 1: VERIFICAR CUENTA (Capturas 1 y 5) */}
                  <div id="card-step-1" className="pn bg-white border-2 border-[var(--line)] p-5 sm:p-6 space-y-4 shadow-sm">
                    <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3 gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span
                          id="badge-step-1"
                          className={`w-7 h-7 flex items-center justify-center text-xs font-black font-heading shrink-0 ${
                            currentStep > 1
                              ? 'bg-[#059669] text-white'
                              : 'bg-[#1447e6] text-white'
                          }`}
                          style={{ clipPath: 'var(--cut)' }}
                        >
                          {currentStep > 1 ? <Check className="w-4 h-4 stroke-[3]" /> : '1'}
                        </span>
                        <h2 className="text-base font-black uppercase tracking-wider text-[#06173f] truncate font-heading">
                          VERIFICAR CUENTA
                        </h2>
                      </div>
                      {currentStep > 1 && (
                        <button
                          type="button"
                          id="btn-edit-step-1"
                          onClick={() => setCurrentStep(1)}
                          className="text-xs text-[#1447e6] hover:underline font-bold cursor-pointer font-heading flex items-center gap-1.5"
                          title="Modificar cuenta seleccionada"
                        >
                          <PenSquare className="w-3.5 h-3.5" />
                          <span>Cambiar</span>
                        </button>
                      )}
                    </div>

                    {currentStep === 1 ? (
                      <div id="body-step-1" className="space-y-4">
                        <p className="text-xs text-[var(--soft)] leading-relaxed font-medium">
                          Comprobaremos que haya disponibilidad antes de seguir con la compra.
                        </p>

                        {/* Selector de Cuenta de Fortnite (en el flujo normal, no absolute para no ser cortado) */}
                        <div className="space-y-2">
                          <button
                            type="button"
                            onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                            className="w-full p-3 bg-white border-2 border-[var(--line)] flex items-center justify-between hover:border-[#1447e6] transition text-left cursor-pointer"
                            style={{ clipPath: 'var(--cut)' }}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div
                                className="w-9 h-9 bg-[var(--ice)] text-[#1447e6] flex items-center justify-center shrink-0 font-heading"
                                style={{ clipPath: 'var(--cut)' }}
                              >
                                <Gamepad2 className="w-5 h-5" />
                              </div>
                              <div className="flex items-center gap-2 truncate">
                                <span className="font-heading font-black text-sm text-[#06173f]">
                                  {selectedAccountUsername || user.fortnite_username || 'godblezzed'}
                                </span>
                                <span
                                  className="px-2 py-0.5 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] text-[10px] font-bold font-heading uppercase"
                                  style={{ clipPath: 'var(--cut)' }}
                                >
                                  PRINCIPAL
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-[var(--soft)] shrink-0 pl-2">
                              <span className="text-xs font-bold text-[#1447e6] hidden sm:inline font-heading uppercase">
                                Cambiar cuenta
                              </span>
                              <div className="w-8 h-8 bg-[var(--ice)] border border-[var(--line)] flex items-center justify-center text-[#1447e6]">
                                {isAccountDropdownOpen ? (
                                  <ChevronUp className="w-4 h-4 text-[#1447e6]" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-[#1447e6]" />
                                )}
                              </div>
                            </div>
                          </button>

                          {/* Lista Desplegable dentro del flujo de la página - Sin recorte */}
                          {isAccountDropdownOpen && (
                            <div
                              className="mt-2 p-2.5 bg-white border-2 border-[var(--line)] shadow-lg space-y-1.5 transition-all"
                              style={{ clipPath: 'var(--cut)' }}
                            >
                              <div className="px-2 py-1 text-[10px] uppercase font-bold text-[var(--soft)] tracking-wider flex items-center justify-between border-b border-[var(--line)] pb-1.5 mb-1 font-heading">
                                <span>Selecciona la cuenta destino:</span>
                                <button
                                  type="button"
                                  onClick={() => navigateTo('profile', '/profile.html#vincular-cuentas')}
                                  className="text-[#1447e6] hover:underline font-bold flex items-center gap-1 text-[10px] cursor-pointer"
                                >
                                  <span>Añadir otra</span>
                                </button>
                              </div>

                              {linkedAccounts.length > 0 ? (
                                linkedAccounts.map((acc, idx) => {
                                  const isSelected =
                                    acc.username.toLowerCase() ===
                                    (selectedAccountUsername || user.fortnite_username || '').toLowerCase();
                                  return (
                                    <div
                                      key={idx}
                                      onClick={() => {
                                        setSelectedAccountUsername(acc.username);
                                        setIsAccountDropdownOpen(false);
                                      }}
                                      className={`p-2.5 transition flex items-center justify-between cursor-pointer select-none border-2 ${
                                        isSelected
                                          ? 'bg-blue-50 border-[#1447e6] text-[#06173f]'
                                          : 'hover:bg-[var(--ice)] border-transparent text-[#06173f]'
                                      }`}
                                      style={{ clipPath: 'var(--cut-sm)' }}
                                    >
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <Gamepad2 className="w-4 h-4 text-[#1447e6] shrink-0" />
                                        <span className="font-heading font-black text-xs uppercase truncate">
                                          {acc.username}
                                        </span>
                                        {acc.is_primary && (
                                          <span className="text-[9px] font-bold text-[#1447e6] px-1.5 py-0.5 bg-[#eff6ff] border border-[#bfdbfe]">
                                            PRINCIPAL
                                          </span>
                                        )}
                                      </div>
                                      {isSelected && <Check className="w-4 h-4 text-[#1447e6] stroke-[3]" />}
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="p-2 text-xs text-[var(--soft)] font-medium">
                                  {user.fortnite_username || 'godblezzed'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Enlace al perfil */}
                        <p className="text-xs text-[var(--soft)] font-medium">
                          Puedes vincular otras cuentas desde el{' '}
                          <button
                            type="button"
                            onClick={() => navigateTo('profile', '/profile.html#vincular-cuentas')}
                            className="text-[#1447e6] font-bold hover:underline cursor-pointer"
                          >
                            Perfil
                          </button>
                        </p>

                        {/* Resultado de Verificación de Amistad y Disponibilidad (Conciso y sin repeticiones) */}
                        <div id="verification-result-box" className="pt-0.5">
                          {privacyBlockedMessage ? (
                            <div
                              className="p-2.5 sm:p-3 bg-red-50 border border-red-200 text-xs text-red-900 space-y-1"
                              style={{ clipPath: 'var(--cut)' }}
                            >
                              <div className="flex items-center gap-1.5 font-bold text-red-700">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                <span>Privacidad bloqueada en Fortnite</span>
                              </div>
                              <p className="text-[11px] text-red-800 leading-normal">
                                En Fortnite ve a <b>Ajustes &gt; Cuenta y Privacidad</b> y pon <b>Solicitudes</b> en <b>Cualquiera</b>.
                              </p>
                            </div>
                          ) : !isVerifying && verificationData ? (
                            <div className="space-y-2">
                              {verificationData.status === 'RECHARGE_ONLY' ? (
                                <div
                                  className="p-2.5 sm:p-3 bg-emerald-50 border border-emerald-300 text-xs text-emerald-800 flex items-center gap-2 font-heading"
                                  style={{ clipPath: 'var(--cut)' }}
                                >
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                  <span>Recarga directa: no requiere 48hs de amistad.</span>
                                </div>
                              ) : verificationData.status === 'FRIENDS_READY' && verificationData.eligible ? (
                                <div
                                  className="p-2.5 sm:p-3 bg-emerald-50 border border-emerald-300 text-xs text-emerald-800 flex items-center gap-2 font-heading"
                                  style={{ clipPath: 'var(--cut)' }}
                                >
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                  <span>Disponibilidad confirmada (+48hs de amistad).</span>
                                </div>
                              ) : verificationData.status === 'REQUEST_SENT' ||
                                Boolean((verificationData.pending_bots_count ?? 0) > 0 && !verificationData.eligible) ? (
                                <div
                                  className="p-2.5 sm:p-3 bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-center gap-2 font-heading"
                                  style={{ clipPath: 'var(--cut)' }}
                                >
                                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                                  <span>Tienes solicitudes pendientes. Acéptalas en Fortnite.</span>
                                </div>
                              ) : verificationData.status === 'FRIENDS_WAITING_48H' ? (
                                <div
                                  className="p-2.5 sm:p-3 bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-center gap-2 font-heading"
                                  style={{ clipPath: 'var(--cut)' }}
                                >
                                  <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                                  <span>
                                    Faltan <strong className="text-[#06173f]">{verificationData.hours_remaining ?? 48}h</strong> de amistad para recibir regalos.
                                  </span>
                                </div>
                              ) : verificationData.status === 'NOT_FRIENDS' ? (
                                <div
                                  className="p-2.5 sm:p-3 bg-blue-50 border border-blue-300 text-xs text-blue-900 flex items-center gap-2 font-heading"
                                  style={{ clipPath: 'var(--cut)' }}
                                >
                                  <UserPlus className="w-4 h-4 text-[#1447e6] shrink-0" />
                                  <span>Aún no tienes agregados a nuestros bots en Fortnite.</span>
                                </div>
                              ) : verificationData.status === 'NO_BOT_BALANCE' ||
                                verificationData.status === 'NO_BOT_ITEM_BALANCE' ||
                                verificationData.status === 'BOT_LIMIT_REACHED' ? (
                                <div
                                  className="p-2.5 sm:p-3 bg-red-50 border border-red-300 text-xs text-red-700 flex items-center gap-2 font-heading"
                                  style={{ clipPath: 'var(--cut)' }}
                                >
                                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                                  <span>Sin stock de regalos por hoy. Notifica para reponer.</span>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        {/* Botón Acción Paso 1 */}
                        <button
                          type="button"
                          id="btn-confirm-step-1"
                          onClick={handleStep1Action}
                          disabled={
                            isVerifying ||
                            isSendingBotRequests ||
                            isNotifyingShortage ||
                            step1Mode === 'WAITING_48H' ||
                            step1Mode === 'RESENT' ||
                            step1Mode === 'NOTIFIED'
                          }
                          className={`btn w-full flex items-center justify-center gap-2 font-heading font-black ${
                            step1Mode === 'WAITING_48H' || step1Mode === 'RESENT' || step1Mode === 'NOTIFIED'
                              ? 'opacity-60 cursor-not-allowed select-none bg-slate-200 text-slate-600 border-slate-300'
                              : 'cursor-pointer'
                          }`}
                          style={{ width: '100%' }}
                        >
                          {isVerifying ? (
                            <>
                              <RotateCw className="w-4 h-4 animate-spin" />
                              <span>Comprobando...</span>
                            </>
                          ) : isSendingBotRequests ? (
                            <>
                              <RotateCw className="w-4 h-4 animate-spin" />
                              <span>Enviando solicitudes...</span>
                            </>
                          ) : isNotifyingShortage ? (
                            <>
                              <RotateCw className="w-4 h-4 animate-spin" />
                              <span>Notificando...</span>
                            </>
                          ) : step1Mode === 'WAITING_48H' ? (
                            <>
                              <Clock className="w-4 h-4" />
                              <span>Esperando 48hs de Amistad</span>
                            </>
                          ) : step1Mode === 'ADD_BOTS' ? (
                            <>
                              <UserPlus className="w-4 h-4" />
                              <span>Agregar bots</span>
                            </>
                          ) : step1Mode === 'REQUESTS_SENT' ? (
                            <>
                              <RotateCw className="w-4 h-4" />
                              <span>Reenviar</span>
                            </>
                          ) : step1Mode === 'RESENT' ? (
                            <>
                              <Check className="w-4 h-4 stroke-[3]" />
                              <span>Reenviado</span>
                            </>
                          ) : step1Mode === 'NOTIFY' ? (
                            <>
                              <Bell className="w-4 h-4" />
                              <span>Notificar</span>
                            </>
                          ) : step1Mode === 'NOTIFIED' ? (
                            <>
                              <Check className="w-4 h-4 stroke-[3]" />
                              <span>Notificado</span>
                            </>
                          ) : (
                            <>
                              <span>CONFIRMAR CUENTA Y CONTINUAR</span>
                              <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      /* Resumen Colapsado Paso 1 (Captura 5 1:1) */
                      <div
                        id="summary-step-1"
                        className="p-3.5 bg-[#ecfdf5] border border-[#a7f3d0] text-[#065f46] text-xs font-heading font-medium flex items-center justify-between"
                        style={{ clipPath: 'var(--cut)' }}
                      >
                        <span id="summary-step-1-text">
                          Fortnite: <strong>{selectedAccountUsername || user.fortnite_username || 'godblezzed'}</strong> • Disponibilidad confirmada
                        </span>
                      </div>
                    )}
                  </div>

                  {/* PASO 2: MÉTODO DE PAGO (Capturas 2 y 5) */}
                  <div
                    id="card-step-2"
                    className={`pn bg-white border-2 border-[var(--line)] p-5 sm:p-6 space-y-4 shadow-sm transition ${
                      currentStep < 2 ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3 gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span
                          id="badge-step-2"
                          className={`w-7 h-7 flex items-center justify-center text-xs font-black font-heading shrink-0 ${
                            currentStep > 2
                              ? 'bg-[#059669] text-white'
                              : currentStep === 2
                              ? 'bg-[#1447e6] text-white'
                              : 'bg-slate-300 text-slate-700'
                          }`}
                          style={{ clipPath: 'var(--cut)' }}
                        >
                          {currentStep > 2 ? <Check className="w-4 h-4 stroke-[3]" /> : '2'}
                        </span>
                        <h2 className="text-base font-black uppercase tracking-wider text-[#06173f] truncate font-heading">
                          MÉTODO DE PAGO
                        </h2>
                      </div>
                      {currentStep > 2 && (
                        <button
                          type="button"
                          id="btn-edit-step-2"
                          onClick={() => setCurrentStep(2)}
                          className="text-xs text-[#1447e6] hover:underline font-bold cursor-pointer font-heading flex items-center gap-1.5"
                          title="Modificar método de pago"
                        >
                          <PenSquare className="w-3.5 h-3.5" />
                          <span>Cambiar</span>
                        </button>
                      )}
                    </div>

                    {currentStep === 2 ? (
                      <div id="body-step-2" className="space-y-3">
                        {/* Opción 1: Saldo de Cuenta */}
                        <div
                          onClick={() => {
                            if (!hasRecharge && isWalletEligible) {
                              setUserManuallySelectedMethod(false);
                              setSelectedPaymentMethod('SALDO_BILLETERA');
                            }
                          }}
                          className={`row-card p-3.5 sm:p-4 transition-all flex items-center justify-between gap-2.5 border-2 ${
                            selectedPaymentMethod === 'SALDO_BILLETERA'
                              ? 'border-[#1447e6] bg-[#f8fafc]'
                              : 'border-[var(--line)] bg-white'
                          } ${
                            hasRecharge || !isWalletEligible
                              ? 'opacity-60 cursor-not-allowed'
                              : 'cursor-pointer hover:border-slate-300'
                          }`}
                          style={{ clipPath: 'var(--cut)' }}
                        >
                          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                            <div className="w-5 h-5 shrink-0 min-w-[20px] min-h-[20px] rounded-full border-2 border-[var(--soft)] flex items-center justify-center">
                              {selectedPaymentMethod === 'SALDO_BILLETERA' && (
                                <div className="w-2.5 h-2.5 rounded-full bg-[#1447e6]" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 min-w-0">
                              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 shrink-0" />
                              <div className="min-w-0">
                                <span className="text-xs sm:text-sm font-bold text-[#06173f] whitespace-nowrap block">
                                  Saldo de cuenta
                                </span>
                                {hasRecharge && (
                                  <span className="text-[10px] text-amber-700 font-medium block">
                                    No aplica para recargas de saldo o suscripciones
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {hasRecharge ? (
                            <span
                              className="shrink-0 px-2 py-0.5 sm:px-2.5 sm:py-1 bg-amber-50 text-amber-700 border border-amber-200 text-[9.5px] sm:text-[10px] font-heading font-black uppercase"
                              style={{ clipPath: 'var(--cut)' }}
                            >
                              NO APLICA PARA RECARGAS
                            </span>
                          ) : !isWalletEligible ? (
                            <span
                              className="shrink-0 px-2 py-0.5 sm:px-2.5 sm:py-1 bg-slate-100 text-slate-500 border border-slate-300 text-[9.5px] sm:text-[10px] font-heading font-black uppercase tracking-wider"
                              style={{ clipPath: 'var(--cut)' }}
                            >
                              SALDO INSUFICIENTE
                            </span>
                          ) : (
                            <span
                              className="shrink-0 px-2 py-0.5 sm:px-2.5 sm:py-1 bg-[#dcfce7] text-[#15803d] border border-[#86efac] text-[9.5px] sm:text-[10px] font-heading font-black uppercase"
                              style={{ clipPath: 'var(--cut)' }}
                            >
                              DISPONIBLE
                            </span>
                          )}
                        </div>

                        {/* Opción 2: Transferencia Bancaria (Activa por defecto, Captura 2) */}
                        <div
                          onClick={() => {
                            setUserManuallySelectedMethod(true);
                            setSelectedPaymentMethod('TRANSFERENCIA_BANCARIA');
                          }}
                          className={`row-card p-3.5 sm:p-4 transition-all space-y-2.5 cursor-pointer border-2 ${
                            selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA'
                              ? 'border-[#1447e6] bg-[#f8fafc]'
                              : 'border-[var(--line)] bg-white'
                          }`}
                          style={{ clipPath: 'var(--cut)' }}
                        >
                          <div className="flex items-center gap-2.5 sm:gap-3">
                            <div className="w-5 h-5 shrink-0 min-w-[20px] min-h-[20px] rounded-full border-2 border-[#1447e6] flex items-center justify-center">
                              {selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' && (
                                <div className="w-2.5 h-2.5 rounded-full bg-[#1447e6]" />
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#1447e6] shrink-0" />
                              <span className="text-xs sm:text-sm font-bold text-[#06173f]">
                                Transferencia bancaria
                              </span>
                            </div>
                          </div>
                          <p className="text-[11.5px] sm:text-xs text-[var(--soft)] sm:pl-7 leading-relaxed font-medium">
                            Los datos bancarios y el comprobante de pago se gestionan en el siguiente paso.
                          </p>

                          {/* Si puede pagar completamente con saldo: botón para pagar con saldo */}
                          {selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' && isWalletEligible && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="w-full sm:ml-7 sm:w-[calc(100%-1.75rem)] p-3 bg-emerald-50 border-2 border-emerald-300 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs text-emerald-900"
                              style={{ clipPath: 'var(--cut)' }}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Wallet className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span className="font-bold">Tienes saldo suficiente para pagar este pedido.</span>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUserManuallySelectedMethod(false);
                                  setSelectedPaymentMethod('SALDO_BILLETERA');
                                }}
                                className="btn sm self-start sm:self-auto cursor-pointer font-heading font-black text-xs"
                                style={{ background: '#1447e6', color: '#fff' }}
                              >
                                Pagar con saldo
                              </button>
                            </div>
                          )}

                          {/* Split Wallet: Aplicar saldo disponible */}
                          {selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' && canUseSplitWallet && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setUseWalletBalance(!useWalletBalance);
                              }}
                              className="w-full sm:ml-7 sm:w-[calc(100%-1.75rem)] p-3 sm:p-3.5 bg-[#f0f9ff]/60 border-2 border-[#5ccbff] space-y-2 transition-all cursor-pointer shadow-xs"
                              style={{ clipPath: 'var(--cut)' }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <input
                                    type="checkbox"
                                    id="chk-use-wallet"
                                    checked={useWalletBalance}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setUseWalletBalance(e.target.checked);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-4 h-4 rounded accent-[#1447e6] cursor-pointer shrink-0"
                                  />
                                  <label
                                    htmlFor="chk-use-wallet"
                                    className="text-xs font-bold text-[#06173f] cursor-pointer select-none truncate"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Usar saldo disponible ({vbucksToUse.toLocaleString('es-AR')} G-Coins)
                                  </label>
                                </div>
                                {useWalletBalance ? (
                                  <span className="shrink-0 text-[10px] font-black font-heading uppercase text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5">
                                    Aplicado
                                  </span>
                                ) : (
                                  <span className="shrink-0 text-[10px] font-bold font-heading uppercase text-slate-400">
                                    No aplicado
                                  </span>
                                )}
                              </div>

                              {useWalletBalance && (
                                <div className="text-xs space-y-1.5 border-t border-[var(--line)] pt-2 text-[#06173f]">
                                  <div className="flex justify-between items-center text-[var(--soft)]">
                                    <span>Saldo descontado:</span>
                                    <span className="text-[#1447e6] font-bold font-heading">
                                      -${walletMoneyDiscount.toLocaleString('es-AR')} {currency}
                                    </span>
                                  </div>
                                  {appliedDiscount && (
                                    <div className="flex justify-between items-center text-emerald-600 font-bold">
                                      <span>
                                        Cupón {appliedDiscount.code} (-{appliedDiscount.discount_pct}%):
                                      </span>
                                      <span className="font-heading">
                                        -${calculatedDiscountAmount.toLocaleString('es-AR')} {currency}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex justify-between items-center font-bold text-[#06173f] border-t border-[var(--line)] pt-1.5 mt-1">
                                    <span>Total a transferir:</span>
                                    <span className="text-sm text-[#1447e6] font-black font-heading">
                                      ${finalPayableFiat.toLocaleString('es-AR')} {currency}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Botón Continuar Paso 2 */}
                        <button
                          type="button"
                          id="btn-confirm-step-2"
                          onClick={handleConfirmStep2}
                          className="btn w-full mt-3 flex items-center justify-center gap-2 cursor-pointer font-heading font-black"
                          style={{ width: '100%' }}
                        >
                          <span>CONTINUAR</span>
                          <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                        </button>
                      </div>
                    ) : currentStep > 2 ? (
                      /* Resumen Colapsado Paso 2 (Captura 5 1:1) */
                      <div
                        id="summary-step-2"
                        className="p-3.5 bg-[#ecfdf5] border border-[#a7f3d0] text-[#065f46] text-xs font-heading font-medium flex items-center justify-between"
                        style={{ clipPath: 'var(--cut)' }}
                      >
                        <span id="summary-step-2-text">
                          Método:{' '}
                          <strong>
                            {selectedPaymentMethod === 'SALDO_BILLETERA'
                              ? 'Saldo de cuenta'
                              : 'Transferencia bancaria'}
                          </strong>
                          {selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' &&
                            canUseSplitWallet &&
                            useWalletBalance &&
                            vbucksToUse > 0 && (
                              <span className="ml-1 text-[#065f46]">
                                (con -{vbucksToUse.toLocaleString('es-AR')} G-Coins aplicados)
                              </span>
                            )}
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--soft)] font-medium">
                        Completa el Paso 1 para seleccionar el método de pago.
                      </p>
                    )}
                  </div>

                  {/* PASO 3: CONFIRMACIÓN DEL PEDIDO (Captura 5 1:1) */}
                  <div
                    id="card-step-3"
                    className={`pn bg-white border-2 border-[var(--line)] p-5 sm:p-6 space-y-4 shadow-sm transition ${
                      currentStep < 3 ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
                      <div className="flex items-center gap-2.5">
                        <span
                          id="badge-step-3"
                          className={`w-7 h-7 flex items-center justify-center text-xs font-black font-heading shrink-0 ${
                            currentStep === 3
                              ? 'bg-[#1447e6] text-white'
                              : 'bg-slate-300 text-slate-700'
                          }`}
                          style={{ clipPath: 'var(--cut)' }}
                        >
                          3
                        </span>
                        <h2 className="text-base font-black uppercase tracking-wider text-[#06173f] font-heading">
                          CONFIRMACIÓN DEL PEDIDO
                        </h2>
                      </div>
                    </div>

                    {currentStep === 3 ? (
                      <div id="body-step-3" className="space-y-4">
                        {/* Resumen de entrega 1:1 con Captura 5 */}
                        <div
                          className="p-4 bg-[var(--ice)] border border-[var(--line)] space-y-2.5 text-xs"
                          style={{ clipPath: 'var(--cut)' }}
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-[var(--soft)] font-medium">Cuenta de Fortnite</span>
                            <strong className="text-[#06173f] font-heading text-sm">
                              {selectedAccountUsername || user.fortnite_username || 'godblezzed'}
                            </strong>
                          </div>
                          <div className="flex justify-between items-center border-t border-[var(--line)] pt-2">
                            <span className="text-[var(--soft)] font-medium">Objetos a recibir</span>
                            <span className="text-[#06173f] font-bold">
                              {totalItemCount} {totalItemCount === 1 ? 'regalo' : 'regalos'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center border-t border-[var(--line)] pt-2">
                            <span className="text-[var(--soft)] font-medium">Precio a pagar</span>
                            <strong className="text-[#1447e6] font-heading text-base">
                              ${displayFinalPrice.toLocaleString('es-AR')} {currency}
                            </strong>
                          </div>
                          {selectedPaymentMethod === 'TRANSFERENCIA_BANCARIA' &&
                            canUseSplitWallet &&
                            useWalletBalance &&
                            vbucksToUse > 0 && (
                              <div className="flex justify-between items-center border-t border-[var(--line)] pt-2">
                                <span className="text-[var(--soft)] font-medium">Saldo descontado</span>
                                <strong className="text-[#1447e6] font-heading">
                                  -{vbucksToUse.toLocaleString('es-AR')} G-Coins (-$
                                  {walletMoneyDiscount.toLocaleString('es-AR')} {currency})
                                </strong>
                              </div>
                            )}
                          <div className="flex justify-between items-center border-t border-[var(--line)] pt-2">
                            <span className="text-[var(--soft)] font-medium">Medio de pago</span>
                            <strong className="text-[#06173f] font-heading">
                              {selectedPaymentMethod === 'SALDO_BILLETERA'
                                ? 'Saldo de cuenta'
                                : canUseSplitWallet && useWalletBalance && vbucksToUse > 0
                                ? 'Transferencia bancaria (con saldo parcial)'
                                : 'Transferencia bancaria'}
                            </strong>
                          </div>
                        </div>

                        {orderError && (
                          <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-xs font-bold flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            <span>{orderError}</span>
                          </div>
                        )}

                        {/* Botón Ir al Pago */}
                        <button
                          type="button"
                          id="btn-confirm-order-redirect"
                          onClick={handleFinalSubmitOrder}
                          disabled={isSubmittingOrder}
                          className="btn w-full mt-2 flex items-center justify-center gap-2 cursor-pointer font-heading font-black"
                          style={{ width: '100%' }}
                        >
                          {isSubmittingOrder ? (
                            <>
                              <RotateCw className="w-4 h-4 animate-spin" />
                              <span>Generando pedido...</span>
                            </>
                          ) : (
                            <>
                              <span>
                                {selectedPaymentMethod === 'SALDO_BILLETERA'
                                  ? 'Confirmar y Pagar'
                                  : `Ir al Pago ($${displayFinalPrice.toLocaleString('es-AR')} ${currency})`}
                              </span>
                              <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--soft)] font-medium">
                        Completa el Paso 2 para proceder al pago.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MODAL SELECCIONAR MONEDA (1:1 con frontend/cart.html:638-703) */}
      {(isCurrencyModalOpen || isCurrencyModalClosing) && (
        <div
          className={`fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-4 overscroll-contain select-none ${
            isCurrencyModalClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
          }`}
          onClick={closeCurrencyModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`max-w-sm w-full bg-white text-[#06173f] p-5 sm:p-6 space-y-4 relative border-2 border-[var(--line)] shadow-2xl ${
              isCurrencyModalClosing ? 'anim-modal-out' : 'anim-modal-in'
            }`}
            style={{
              clipPath:
                'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b-2 border-[var(--line)] pb-3">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-9 h-9 bg-[var(--ice)] text-[#1447e6] flex items-center justify-center text-sm font-black font-heading shrink-0"
                  style={{ clipPath: 'var(--cut)' }}
                >
                  <Wallet className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase text-[#06173f] tracking-wide font-heading">
                    Moneda de la Cuenta
                  </h3>
                  <p className="text-[11px] text-[var(--soft)] font-medium">
                    Selecciona tu moneda de preferencia
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeCurrencyModal}
                className="w-7 h-7 flex items-center justify-center bg-white hover:bg-slate-100 border border-[var(--line)] text-slate-500 hover:text-[#06173f] transition cursor-pointer"
                style={{ clipPath: 'var(--cut-sm)' }}
                title="Cerrar"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Opciones */}
            <div className="space-y-2.5">
              {/* ARS */}
              <div
                onClick={() => setTempCurrency('ARS')}
                className={`p-3.5 border-2 transition-all flex items-center justify-between cursor-pointer ${
                  tempCurrency === 'ARS'
                    ? 'border-[#1447e6] bg-[#eff6ff]'
                    : 'border-[var(--line)] bg-white hover:border-slate-400'
                }`}
                style={{ clipPath: 'var(--cut)' }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none">🇦🇷</span>
                  <div>
                    <div className="text-xs font-black text-[#06173f] flex items-center gap-1.5 font-heading">
                      <span>ARS</span>
                      <span className="text-[10px] text-[var(--soft)] font-medium">· Peso Argentino</span>
                    </div>
                    <p className="text-[11px] text-[var(--soft)] font-medium">Transferencia bancaria local</p>
                  </div>
                </div>
                <div className="w-5 h-5 shrink-0 min-w-[20px] min-h-[20px] rounded-full border-2 border-[#1447e6] flex items-center justify-center">
                  {tempCurrency === 'ARS' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-[#1447e6]" />
                  )}
                </div>
              </div>

              {/* MXN */}
              <div
                onClick={() => setTempCurrency('MXN')}
                className={`p-3.5 border-2 transition-all flex items-center justify-between cursor-pointer ${
                  tempCurrency === 'MXN'
                    ? 'border-[#1447e6] bg-[#eff6ff]'
                    : 'border-[var(--line)] bg-white hover:border-slate-400'
                }`}
                style={{ clipPath: 'var(--cut)' }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none">🇲🇽</span>
                  <div>
                    <div className="text-xs font-black text-[#06173f] flex items-center gap-1.5 font-heading">
                      <span>MXN</span>
                      <span className="text-[10px] text-[var(--soft)] font-medium">· Peso Mexicano</span>
                    </div>
                    <p className="text-[11px] text-[var(--soft)] font-medium">Transferencia SPEI / Débito</p>
                  </div>
                </div>
                <div className="w-5 h-5 shrink-0 min-w-[20px] min-h-[20px] rounded-full border-2 border-[#1447e6] flex items-center justify-center">
                  {tempCurrency === 'MXN' && (
                    <div className="w-2.5 h-2.5 rounded-full bg-[#1447e6]" />
                  )}
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="grid grid-cols-2 gap-2.5 pt-1">
              <button
                type="button"
                onClick={closeCurrencyModal}
                className="btn lt sm w-full cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  setCurrency(tempCurrency);
                  closeCurrencyModal();
                }}
                className="btn sm w-full flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <span>Guardar</span>
                <Check className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
